/**
 * Vista 6 · Expediente — nivel 2.
 *
 * `NEG-1`: la sección 1 (Selección) es **verificación interna y no genera
 * requerimiento**. La pantalla lo dice literalmente en su subtítulo, porque es
 * la distinción que separa «lo comprobamos nosotros» de «hay que preguntarle a
 * la entidad», y confundirlas sería el error más caro de esta pantalla.
 *
 * Al verificar un ítem se guarda **fecha y responsable**, no un booleano
 * anónimo: corrige `P-17` del prototipo.
 */
import { useState } from 'react'
import { Avatar, Button, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@mpfn/ui'
import { DataTable, EmptyState, KeyValueGrid, type DataTableColumn } from '@mpfn/patterns'
import { Check, FileText, Plus, Send } from 'lucide-react'
import { useExpediente, useMemorandos, useVerificarItem } from '../api/consultas.js'
import { useCatalogos } from '../api/catalogos.js'
import { urlDeArchivo } from '../api/cliente.js'
import type { DocumentoDeclarado, ItemSeleccion, SeccionVerificable } from '../api/tipos.js'
import { Breadcrumb } from '../componentes/Breadcrumb.js'
import { Datos } from '../componentes/Datos.js'
import { FiscalizacionDelServidor } from '../componentes/FiscalizacionDelServidor.js'
import { Pagina } from '../componentes/Pagina.js'
import { Seguimiento } from '../componentes/Seguimiento.js'
import { SeguimientoDeDocumento } from '../componentes/SeguimientoDeDocumento.js'
import { irA } from '../navegacion/Enlace.js'
import { usePuede } from '../sesion/contexto.js'
import { useUbicacion } from '../navegacion/useUbicacion.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { dni as formatearDni, fecha, iniciales, periodo as formatearPeriodo } from '../lib/formato.js'
import { DialogoDocumento } from './DialogoDocumento.js'
import { DialogoEmision } from './DialogoEmision.js'

export function Expediente({ id }: { id: string }) {
  const catalogos = useCatalogos()
  const expediente = useExpediente(id)
  const ubicacion = useUbicacion()
  const puedeVerificar = usePuede('seleccion.verificar')
  const puedeRegistrar = usePuede('documento_declarado.registrar')
  const memorandos = useMemorandos(id)
  // Culminada la fiscalización, el memorando de resultados ya fijó qué se
  // fiscalizó: no se ofrece agregar documentos (el servidor tampoco lo admite).
  const culminado = memorandos.data?.culminado ?? false
  const [seccionEnAlta, setSeccionEnAlta] = useState<SeccionVerificable | undefined>(undefined)

  // De dónde se abrió: permite volver al bloque correcto en la miga.
  const periodoOrigen = (ubicacion.state as { periodo?: string } | null)?.periodo

  return (
    <Pagina>
      <Datos estado={expediente}>
        {(datos) => (
          <>
            <Breadcrumb
              migas={[
                ...(periodoOrigen
                  ? [
                      {
                        etiqueta: `Bloque ${formatearPeriodo(periodoOrigen)}`,
                        vista: 'bloque' as const,
                        parametros: { periodo: periodoOrigen },
                      },
                    ]
                  : []),
                { etiqueta: datos.expediente.nombres },
              ]}
            />

            <Card>
              <CardHeader className="sigfisc-identidad">
                <Avatar fallback={iniciales(datos.expediente.nombres)} alt="" />
                <div className="sigfisc-identidad__cuerpo">
                  <CardTitle>{datos.expediente.nombres}</CardTitle>
                  <KeyValueGrid
                    items={[
                      { label: 'DNI', value: formatearDni(datos.expediente.dni) },
                      { label: 'Régimen', value: datos.expediente.regimen },
                      { label: 'Distrito fiscal', value: datos.expediente.distrito },
                      { label: 'Puesto', value: datos.expediente.cargo ?? '—' },
                    ]}
                  />
                </div>
              </CardHeader>
            </Card>

            {/* La fila del padrón de la que salió, entera: motivo, convocatoria,
                fecha de inicio, dependencia, correos… Los expedientes creados
                antes de guardarla la traen vacía y no muestran la tarjeta. */}
            {datos.expediente.datosPadron !== undefined && datos.expediente.datosPadron.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Datos del padrón de altas</CardTitle>
                </CardHeader>
                <CardContent>
                  <KeyValueGrid
                    items={datos.expediente.datosPadron.map((celda) => ({
                      label: celda.columna,
                      value: celda.valor || '—',
                    }))}
                  />
                </CardContent>
              </Card>
            )}

            <SeccionSeleccion
              items={datos.seleccion}
              editable={puedeVerificar}
            />

            {catalogos.secciones.map((opcion) => {
              const documentos =
                datos.secciones.find((item) => item.seccion === opcion.codigo)?.documentos ?? []
              return (
                <SeccionDocumentos
                  key={opcion.codigo}
                  ordinal={opcion.ordinal}
                  titulo={opcion.etiqueta}
                  documentos={documentos}
                  editable={puedeRegistrar && !culminado}
                  onAgregar={() => {
                    setSeccionEnAlta(opcion.codigo)
                  }}
                />
              )
            })}

            <FiscalizacionDelServidor expedienteId={id} nombre={datos.expediente.nombres} />

            {seccionEnAlta !== undefined && (
              <DialogoDocumento
                expedienteId={id}
                seccion={seccionEnAlta}
                abierto
                onCambio={(abierto) => {
                  if (!abierto) setSeccionEnAlta(undefined)
                }}
              />
            )}
          </>
        )}
      </Datos>
    </Pagina>
  )
}

function SeccionSeleccion({
  items,
  editable,
}: {
  items: readonly ItemSeleccion[]
  editable: boolean
}) {
  const verificar = useVerificarItem()
  const verificados = items.filter((item) => item.verificadoEn !== undefined).length

  return (
    <Card>
      <CardHeader className="sigfisc-seccion-card__cabecera">
        <div>
          <CardTitle>1 · Selección</CardTitle>
          <p className="sigfisc-nota">Verificación interna · sin requerimiento</p>
        </div>
        <StatusBadge
          tone={items.length > 0 && verificados === items.length ? 'good' : 'neutral'}
          label={`${String(verificados)}/${String(items.length)}`}
        />
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <EmptyState title="Este expediente no tiene ítems de Selección" />
        ) : (
          <ul className="sigfisc-items">
            {items.map((item) => (
              <li key={item.id} className="sigfisc-items__fila">
                <span className="sigfisc-items__denominacion">
                  {item.orden}. {item.denominacion}
                  {item.exigeEvidencia && !item.verificadoEn && (
                    <span className="sigfisc-items__marca">exige evidencia</span>
                  )}
                </span>

                {item.verificadoEn !== undefined ? (
                  <span className="sigfisc-items__verificado">
                    <Check size={14} aria-hidden="true" />
                    Verificado · {fecha(item.verificadoEn)}
                    {item.evidenciaId !== undefined && (
                      <a className="mpfn-control" href={urlDeArchivo(item.evidenciaId)} download>
                        evidencia
                      </a>
                    )}
                  </span>
                ) : editable ? (
                  <MarcarItem
                    item={item}
                    pendiente={verificar.isPending && verificar.variables?.itemId === item.id}
                    onMarcar={(archivo) => {
                      verificar.mutate(
                        { itemId: item.id, ...(archivo ? { archivo } : {}) },
                        {
                          onSuccess: () => {
                            avisarExito('Ítem verificado.')
                          },
                          onError: (error) => {
                            avisarError(error, 'No se pudo verificar el ítem')
                          },
                        },
                      )
                    }}
                  />
                ) : (
                  <span className="sigfisc-items__pendiente">Pendiente</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Un ítem que exige evidencia necesita el archivo ANTES de poder marcarse
 * (`NEG-3`), así que abre un selector; los demás se marcan de un clic.
 */
function MarcarItem({
  item,
  pendiente,
  onMarcar,
}: {
  item: ItemSeleccion
  pendiente: boolean
  onMarcar: (archivo?: File) => void
}) {
  if (!item.exigeEvidencia) {
    return (
      <Button
        variant="secondary"
        size="sm"
        loading={pendiente}
        onClick={() => {
          onMarcar()
        }}
      >
        Marcar verificado
      </Button>
    )
  }

  return (
    <label className="sigfisc-items__adjuntar mpfn-control">
      <input
        type="file"
        accept=".pdf,image/*"
        className="mpfn-sr-only"
        onChange={(evento) => {
          const archivo = evento.target.files?.[0]
          if (archivo) onMarcar(archivo)
          evento.target.value = ''
        }}
      />
      <Plus size={14} aria-hidden="true" />
      Adjuntar y verificar
    </label>
  )
}

function SeccionDocumentos({
  ordinal,
  titulo,
  documentos,
  editable,
  onAgregar,
}: {
  ordinal: number
  titulo: string
  documentos: readonly DocumentoDeclarado[]
  editable: boolean
  onAgregar: () => void
}) {
  const puedeEmitir = usePuede('requerimiento.emitir')
  const [emitiendoEn, setEmitiendoEn] = useState<DocumentoDeclarado | undefined>(undefined)
  const vigentes = documentos.filter((documento) => !documento.descartado)

  const columnas: ReadonlyArray<DataTableColumn<DocumentoDeclarado>> = [
    { id: 'denominacion', header: 'Documento', cell: (fila) => fila.denominacion },
    {
      id: 'entidad',
      header: 'Entidad',
      hideOnMobile: true,
      cell: (fila) => fila.entidadTexto ?? (fila.entidadId !== undefined ? 'Del catálogo' : '—'),
    },
    /**
     * El estado, y **detrás de él el circuito que llevó hasta ahí**.
     *
     * El disparador es la propia insignia y no un indicador aparte. Dos
     * motivos, y el primero es el que manda:
     *
     *  · «Verificado» es la conclusión y el circuito es cómo se llegó a ella;
     *    la explicación va detrás de lo que explica. Es el mismo gesto que un
     *    nivel más arriba, donde se posa el puntero sobre el chip S·F·E·C.
     *  · El tono y la etiqueta los calcula el servidor para el documento
     *    (`ARQ-4`). Una columna aparte obligaba a inventar un indicador neutro
     *    porque el agregado «no existía» — existía, y era este.
     *
     * `sin_requerir` no dispara nada: no hay requerimiento emitido, así que no
     * hay recorrido que enseñar y un control que abre un panel vacío engaña.
     */
    {
      id: 'estado',
      header: 'Estado',
      cell: (fila) => {
        const insignia = <StatusBadge tone={fila.tono} label={fila.etiqueta} />
        if (fila.estado === 'sin_requerir') return insignia
        return (
          <Seguimiento
            etiqueta={`${fila.denominacion}: ${fila.etiqueta}. Ver el circuito que llevó a este estado`}
            disparador={insignia}
          >
            <SeguimientoDeDocumento documento={fila} />
          </Seguimiento>
        )
      },
    },
    /**
     * `ARQ-4`: quién puede emitir lo decide el servidor —`acciones` y
     * `puedeEmitir` ya vienen en la proyección—, y esta columna solo los pinta.
     * El permiso se comprueba igual porque un botón que el usuario no puede
     * usar es ruido, no información.
     */
    {
      id: 'accion',
      header: 'Acción',
      align: 'right',
      cell: (fila) =>
        puedeEmitir &&
        fila.acciones.some(
          (accion) => accion === 'emitir_requerimiento' || accion === 'reiterar',
        ) ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={!fila.puedeEmitir}
            onClick={(evento) => {
              // La fila entera navega al documento; este botón no.
              evento.stopPropagation()
              setEmitiendoEn(fila)
            }}
            // `DataTable` pone Intro y Espacio en el `<tr>` para abrirlo. Sin
            // esto, pulsar el botón con teclado emitía Y navegaba.
            onKeyDown={(evento) => {
              if (evento.key === 'Enter' || evento.key === ' ') evento.stopPropagation()
            }}
          >
            <Send size={14} aria-hidden="true" />
            {fila.ordinalSiguiente > 1 ? `Reiterar ${fila.ordinalSiguiente}` : 'Requerir'}
          </Button>
        ) : null,
    },
  ]

  return (
    <Card>
      <CardHeader className="sigfisc-seccion-card__cabecera">
        <CardTitle>
          {ordinal} · {titulo}
        </CardTitle>
        {editable && (
          <Button variant="secondary" size="sm" onClick={onAgregar}>
            <Plus size={14} aria-hidden="true" />
            Agregar
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {vigentes.length === 0 ? (
          <EmptyState icon={FileText} title="Sin documentos registrados en esta sección" />
        ) : (
          <DataTable
            columns={columnas}
            rows={vigentes}
            rowKey={(fila) => fila.id}
            onRowClick={(fila) => {
              irA('documento', { id: fila.id })
            }}
            rowActionLabel={(fila) => `Abrir ${fila.denominacion}`}
            empty={{ title: 'Sin documentos' }}
          />
        )}
      </CardContent>

      {/* El mismo diálogo de la vista de documento, sin duplicar nada. */}
      {emitiendoEn && (
        <DialogoEmision
          documento={emitiendoEn}
          abierto
          onCambio={(abierto) => {
            if (!abierto) setEmitiendoEn(undefined)
          }}
        />
      )}
    </Card>
  )
}

